import { useMemo } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type Chart,
  type ChartData,
  type ChartOptions,
  type Plugin,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import type { SpreadHistoricalDto } from '@/api/types/spreadMonitoring';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Tooltip, Filler);

const PALETTE = ['#8fbd56', '#3b82f6', '#f59e0b', '#ef4444', '#14b8a6', '#a855f7', '#64748b'];

// Draws a red up-arrow above each point at/above the spread threshold. Matches the
// legacy spreadThresholdArrows plugin exactly: threshold must be > 0, hidden datasets
// skipped, arrow anchored at the point's tooltipPosition, 20px line (y-50..y-30) with a
// trig-computed arrowhead at y-25, pure red, 2px stroke.
const thresholdArrows: Plugin<'line' | 'bar'> = {
  id: 'spreadThresholdArrows',
  afterDraw(chart: Chart, _args, opts: { threshold?: number | null }) {
    const th = opts?.threshold;
    if (th == null || Number.isNaN(th) || th <= 0) return;
    const { ctx } = chart;
    chart.data.datasets.forEach((ds, di) => {
      const meta = chart.getDatasetMeta(di);
      if (meta.hidden) return;
      meta.data.forEach((pt, i) => {
        const v = ds.data[i] as number | null;
        if (v == null || v < th) return;
        const pos = (pt as unknown as { tooltipPosition: () => { x: number; y: number } }).tooltipPosition();
        const x = pos.x;
        const y = pos.y;
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(x, y - 50);
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.strokeStyle = 'red';
        ctx.lineTo(x, y - 30);
        ctx.stroke();

        const arrowSize = 12;
        const angle = Math.atan2(-30, 0);
        ctx.beginPath();
        ctx.moveTo(x, y - 25);
        ctx.lineTo(x + arrowSize * Math.cos(angle - Math.PI / 5), y - 25 + arrowSize * Math.sin(angle - Math.PI / 5));
        ctx.moveTo(x, y - 25);
        ctx.lineTo(x + arrowSize * Math.cos(angle + Math.PI / 5), y - 25 + arrowSize * Math.sin(angle + Math.PI / 5));
        ctx.stroke();
        ctx.restore();
      });
    });
  },
};

export function SpreadHistoricalChart({
  result,
  type,
  showDots,
  threshold,
}: {
  result: SpreadHistoricalDto;
  type: 'line' | 'bar';
  showDots: boolean;
  threshold: number | null;
}) {
  const data = useMemo<ChartData<'line' | 'bar'>>(() => {
    const byLabel = (pts: { label: string; spreadPoints: number }[]) => {
      const m = new Map(pts.map((p) => [p.label, p.spreadPoints]));
      return result.labels.map((l) => (m.has(l) ? (m.get(l) as number) : null));
    };
    return {
      labels: result.labels,
      datasets: result.datasets.map((d, i) => ({
        label: d.connectorName,
        data: byLabel(d.points),
        borderColor: PALETTE[i % PALETTE.length],
        backgroundColor: `${PALETTE[i % PALETTE.length]}33`,
        tension: 0.2,
        spanGaps: true,
        pointRadius: showDots ? 3 : 0,
        pointHoverRadius: showDots ? 5 : 3,
        borderWidth: 1.5,
      })),
    };
  }, [result, showDots]);

  const options = useMemo<ChartOptions<'line' | 'bar'>>(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: 'bottom' },
        tooltip: { enabled: true },
        spreadThresholdArrows: { threshold },
      },
      scales: {
        x: { grid: { display: false } },
        y: { grid: { color: 'rgba(0,0,0,0.05)' }, title: { display: true, text: 'Spread (pips)' } },
      },
    }),
    [threshold],
  );

  const commonProps = { data: data as never, options: options as never, plugins: [thresholdArrows] as never };
  return <div style={{ height: 380 }}>{type === 'bar' ? <Bar {...commonProps} /> : <Line {...commonProps} />}</div>;
}
